// src/quality-management/contexts/AuthAdapter.tsx

import React, { createContext, useContext, ReactNode } from 'react';
import { useGlobalAuth } from '../../shared/contexts/GlobalAuthContext';

// Quality Management User interface (for compatibility)
interface QualityManagementUser {
  id: number;
  username: string;
  role: 'Project Manager' | 'Employee' | 'Supervisor';
  // Role original do backend (admin/director/manager/supervisor/employee).
  // Use esta para decisões de permissão; `role` existe só por compatibilidade.
  globalRole: string;
  email?: string;
  name?: string;
  employee_id: number; // Add employee_id for task assignment compatibility
}

interface QualityManagementAuthContextType {
  user: QualityManagementUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
}

const QualityManagementAuthContext = createContext<QualityManagementAuthContextType>({
  user: null,
  login: async () => {},
  logout: async () => {},
  loading: false,
  isAuthenticated: false,
});

// Adapter component that converts global auth to Quality Management format
export const QualityManagementAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  try {
    const { user: globalUser, isAuthenticated, logout, loading } = useGlobalAuth();

    // Convert global user to Quality Management user format
    // `director` precisa cair em 'Project Manager' junto com admin/manager —
    // antes caía no else e era rebaixado a 'Employee', perdendo acesso.
    const adaptedUser: QualityManagementUser | null = globalUser ? {
    id: globalUser.id,
    username: globalUser.username,
    role: ['admin', 'manager', 'director'].includes(globalUser.role) ? 'Project Manager' :
          globalUser.role === 'supervisor' ? 'Supervisor' : 'Employee',
    globalRole: globalUser.role,
    email: globalUser.email,
    name: globalUser.name,
    employee_id: (globalUser as any).employee_id || globalUser.id
    } : null;

    const adaptedLogout = async () => {
      logout(); // Global logout doesn't return a promise
    };

    const value: QualityManagementAuthContextType = {
      user: adaptedUser,
      login: async () => { throw new Error('Login deve ser feito pela página de login'); },
      logout: adaptedLogout,
      loading,
      isAuthenticated
    };

    return (
      <QualityManagementAuthContext.Provider value={value}>
        {children}
      </QualityManagementAuthContext.Provider>
    );
  } catch (error) {
    throw error;
  }
};

// Export the context for components to use
export const AuthContext = QualityManagementAuthContext;
export const AuthProvider = QualityManagementAuthProvider;
